// DateInput — input de data brasileiro com máscara DD/MM/AAAA e parser ISO.
// Substitui <Input type="date"> em qualquer campo de data: evita variação de
// formato por OS/locale do browser, força DD/MM/AAAA consistente, e exporta
// ISO yyyy-mm-dd pro API/RHF.
//
// Contrato:
//   value: string (ISO yyyy-mm-dd) ou "" / undefined  ← amigável pra API/RHF
//   onChange: (iso: string | undefined)               ← undefined enquanto
//                                                       digita ou inválido;
//                                                       string ISO quando completo
//                                                       e válido.
//
// Não usa o picker nativo. Caso o admin queira um datepicker, isto fica como
// trabalho futuro (Popover + Calendar visual).
import { forwardRef, useEffect, useState } from 'react';

import { Calendar } from 'lucide-react';

import { dateBRToISO, isoToDateBR, maskDateBR } from '@alamo/utils';

import { Input, type InputProps } from '../Input/Input';

export interface DateInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  /** Valor ISO yyyy-mm-dd. Vazio/undefined renderiza o input em branco. */
  value?: string;
  /** Callback recebe ISO yyyy-mm-dd quando o formato fica completo e válido.
   *  Recebe undefined enquanto a digitação está parcial ou se a data é inválida. */
  onChange?: (iso: string | undefined) => void;
  /** Esconde o ícone de calendário à esquerda. */
  hideIcon?: boolean;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { value, onChange, hideIcon, leftIcon, placeholder, ...rest },
  ref,
) {
  // Texto digitado (BR) — vive local pra que parciais ("12/0") fiquem
  // visíveis enquanto `value` externo segue undefined até a entrada completar.
  const [text, setText] = useState<string>(() => isoToDateBR(value));

  // Re-sincroniza quando `value` externo muda (form reset, switch para edit).
  // Mantém o texto atual se ele já mapeia pro mesmo ISO (evita pisar em cima
  // de digitação parcial quando o pai não mudou nada).
  useEffect(() => {
    const formatted = isoToDateBR(value);
    setText((prev) => {
      const prevIso = dateBRToISO(prev);
      if (prevIso === value) return prev;
      if (!prevIso && !value) return prev;
      return formatted;
    });
  }, [value]);

  const iconNode = hideIcon ? null : (
    <Calendar className="size-4 text-slate-400" aria-hidden="true" />
  );

  return (
    <Input
      {...rest}
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder ?? 'DD/MM/AAAA'}
      leftIcon={leftIcon ?? iconNode ?? undefined}
      value={text}
      onChange={(e) => {
        const masked = maskDateBR(e.target.value);
        setText(masked);
        const iso = dateBRToISO(masked);
        onChange?.(iso);
      }}
    />
  );
});
